import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  CategoryTag,
  CustomFacet,
  CustomFacetContentType,
  CustomFacetValue,
  CustomProductList,
  DefaultProductList,
  RangeFacet,
} from '@catalog/const';
import { CategoryTagsFacade } from '@catalog/data-access';
import { Store } from '@ngrx/store';
import { ActiveFacet, SearchFacetParams } from '@search/const';
import { SearchActions, SearchSelectors, StateWithSearch } from '@search/data-access';
import { RoutingService } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ActiveFacetsService {
  constructor(
    private store: Store<StateWithSearch>,
    private routingService: RoutingService,
    private categoryTagsFacade: CategoryTagsFacade,
  ) {}

  getActiveFacets(): Observable<ActiveFacet[]> {
    return this.store.select(SearchSelectors.getActiveFacetsState);
  }

  getSelectedFacets(): Observable<ActiveFacet[]> {
    return this.store.select(SearchSelectors.getSelectedFacetsState);
  }

  toggleFacet(facet: CustomFacet, value: CustomFacetValue, force?: boolean): void {
    this.store.dispatch(new SearchActions.ToggleFacet({ facet, value, force }));
  }

  toggleSelectedFacet(facet: CustomFacet, value: CustomFacetValue, force?: boolean): void {
    this.store.dispatch(new SearchActions.ToggleSelectedFacet({ facet, value, force }));
  }

  setSelectedFacetValue(facet: CustomFacet, value: string | number | boolean): void {
    this.store.dispatch(new SearchActions.SetSelectedFacetValue({ facet, value }));
  }

  setSelectedFacetRangeValue(facet: RangeFacet, value: (number | null)[]): void {
    this.store.dispatch(new SearchActions.SetSelectedFacetRangeValue({ facet, value }));
  }

  clearFacetValue(facet: CustomFacet | RangeFacet): void {
    this.store.dispatch(new SearchActions.ClearFacetValue({ facet }));
  }

  clearAllFacets(): void {
    this.store.dispatch(new SearchActions.ClearAllFacets());
  }

  hasChangedFacets(): Observable<boolean> {
    return combineLatest({
      activeFacets: this.getActiveFacets(),
      selectedFacets: this.getSelectedFacets(),
    }).pipe(
      map(({ activeFacets, selectedFacets }) => {
        if (activeFacets.length !== selectedFacets.length) {
          return true;
        }

        for (let i = 0; i < selectedFacets.length; i++) {
          if (selectedFacets[i].code !== activeFacets[i].code) {
            return true;
          }
        }

        return false;
      }),
    );
  }

  clearSelectedFacets(): void {
    this.store.dispatch(new SearchActions.ClearSelectedFacets());
  }

  getSelectedFacetParams(): Observable<SearchFacetParams> {
    return combineLatest({
      activeFacets: this.getSelectedFacets(),
      routerState: this.routingService.getRouterState(),
    }).pipe(
      filter(({ routerState }) => Boolean(routerState?.state.semanticRoute)),
      switchMap(({ activeFacets, routerState }) => {
        return this.paramsFromRouteState(routerState.state.semanticRoute, routerState.state.params).pipe(
          map((params: SearchFacetParams) => {
            return { ...params, activeFacets };
          }),
        );
      }),
    );
  }

  paramsFromRouteState(semanticRoute: string, params: Params): Observable<Partial<SearchFacetParams>> {
    const searchFacetParams: Partial<SearchFacetParams> = {};

    switch (semanticRoute) {
      case DefaultProductList.CategoryProductList:
        searchFacetParams.categoryCode = params.categoryCode;
        break;
      case CustomProductList.PromoProductList:
        searchFacetParams.promoCode = params.promoCode;
        break;
      case CustomProductList.SeoProductList:
        return this.categoryTagsFacade.getActiveCategoryTagItem().pipe(
          // при быстром переключении между тегами, может прилететь предыдущий активный тег,
          // тут мы убеждаемся что слаг в урле совпадает с активным тегом
          filter<CategoryTag>((activeTag) => activeTag && activeTag.slug === params.tagSlug),
          take(1),
          map(({ categoryCode, facetFilter, queryParams }: CategoryTag) => {
            if (facetFilter) {
              return { categoryCode, activeFacets: this.extractFacetsFromOldQueryParams(facetFilter) };
            }

            return { categoryCode, activeFacets: this.extractFacetsFromQueryParams(queryParams) };
          }),
        );
        break;
      case CustomProductList.BestOffersProductList:
        searchFacetParams.hasDiscount = true;
        break;
      case CustomProductList.SaleProductList:
        searchFacetParams.sale = true;
        break;
      default:
        break;
    }

    return of(searchFacetParams);
  }

  /**
   * Метод принимает строку с квери параметрами вытаскивает из нее
   * параметр facets и превращает этот параметр в массив активных фасетов
   */
  extractFacetsFromQueryParams(queryParams: string): ActiveFacet[] | undefined {
    const decodedQueryParams = decodeURI(queryParams);
    const urlSearchParams = new URLSearchParams(decodedQueryParams);

    if (!urlSearchParams.has('facets')) {
      return undefined;
    }

    const facets = urlSearchParams.get('facets').split(',');

    return facets.slice(1).map((item) => {
      const [facetCode, valueCode] = item.split(':');

      return {
        contentType: ['priceValue'].includes(facetCode)
          ? CustomFacetContentType.Range
          : CustomFacetContentType.Checkbox,
        name: facetCode,
        code: facetCode,
        value: { name: valueCode, code: valueCode },
      };
    });
  }

  /**
   * @param query Примеры:
   *  `:relevance:allCategories:shtukaturki-5584:facet_attr-osnova-smesi_cl-l3-shtukaturki:Цементная`
   *  `поисковая фраза:relevance:facet_attr-osnova-smesi_cl-l3-shtukaturki:Цементная`
   *  `:price-asc:allCampaigns:promoCode:facet_attr-osnova-smesi_cl-l3-shtukaturki:Цементная`
   */
  extractFacetsFromOldQueryParams(queryParams: string): ActiveFacet[] | undefined {
    const partsQuery = queryParams.split(':');
    // убираем сортировку
    let partsQueryWithoutSort = partsQuery.slice(2);

    if (['allCategories', 'allCampaigns'].includes(partsQueryWithoutSort[0])) {
      // убираем категорию или промо компанию
      partsQueryWithoutSort = partsQueryWithoutSort.slice(2);
    }

    const entries = [];
    while (partsQueryWithoutSort.length) {
      entries.push(partsQueryWithoutSort.splice(0, 2));
    }

    if (entries.length === 0) {
      return undefined;
    }

    // пока так, нужно переделывать ответ бэка тэгов, чтобы он возвращал не query, а что-то типа breadcrumbs в роуте search
    return entries.map(([facetCode, valueCode]) => ({
      contentType: ['priceValue'].includes(facetCode) ? CustomFacetContentType.Range : CustomFacetContentType.Checkbox,
      name: facetCode,
      code: facetCode,
      value: { name: valueCode, code: valueCode },
    }));
  }
}
